import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import PromoBannerModal from './PromoBannerModal'
import { SHOW_PROMO_BANNER_EVENT } from '../utils/promoBannerEvents'

const VISIBLE_MS = 60_000
const REOPEN_MS = 300_000
const MIN_TAB_INTERVAL_MS = 180_000
const MAX_BLOCKS_PER_HOUR = 100
const FLOATING_BANNER_Z_INDEX = 2147483647
const FLOATING_BANNER_STATE_KEY = 'floating_promo_banner_state'
const FLOATING_BANNER_SESSION_KEY = 'floating_promo_banner_session_enabled'
const HOUR_MS = 3_600_000

const getFloatingBoxStyle = (expanded) => {
  const viewportWidth = window.innerWidth || 1024
  const viewportHeight = window.innerHeight || 768

  const width = expanded ? 680 : 345
  const height = expanded ? 351 : 178

  return {
    width: Math.min(width, viewportWidth - 20),
    height: Math.min(height, viewportHeight - 80),
    right: Math.max(10, viewportWidth * 0.002),
    bottom: Math.max(40, viewportHeight * 0.022 + 30)
  }
}

const getStoredState = () => {
  try {
    const state = JSON.parse(
      localStorage.getItem(FLOATING_BANNER_STATE_KEY) || '{}'
    )

    return {
      displayHistory: Array.isArray(state.displayHistory)
        ? state.displayHistory.filter(ts => Number.isFinite(ts))
        : [],
      activeCycleEndsAt: Number.isFinite(state.activeCycleEndsAt)
        ? state.activeCycleEndsAt
        : 0
    }
  } catch {
    return {
      displayHistory: [],
      activeCycleEndsAt: 0
    }
  }
}

const setStoredState = (state) => {
  try {
    localStorage.setItem(
      FLOATING_BANNER_STATE_KEY,
      JSON.stringify(state)
    )
  } catch {}
}

const getSessionEnabled = () => {
  try {
    return sessionStorage.getItem(FLOATING_BANNER_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

const setSessionEnabled = () => {
  try {
    sessionStorage.setItem(FLOATING_BANNER_SESSION_KEY, '1')
  } catch {}
}

const getRecentDisplays = (history, now) => (
  history.filter(ts => now - ts < HOUR_MS)
)

const getAutoDisplayState = (history, now) => {
  const recentDisplays = getRecentDisplays(history, now)

  if (recentDisplays.length >= MAX_BLOCKS_PER_HOUR) {
    return {
      allowed: false,
      recentDisplays
    }
  }

  return {
    allowed: true,
    recentDisplays
  }
}

const getManualDisplayState = (history, now) => {
  const recentDisplays = getRecentDisplays(history, now)
  const lastDisplayTime = recentDisplays[recentDisplays.length - 1] || 0

  if (lastDisplayTime && now - lastDisplayTime < MIN_TAB_INTERVAL_MS) {
    return {
      allowed: false,
      recentDisplays
    }
  }

  if (recentDisplays.length >= MAX_BLOCKS_PER_HOUR) {
    return {
      allowed: false,
      recentDisplays
    }
  }

  return {
    allowed: true,
    recentDisplays
  }
}

const FloatingPromoBanner = () => {
  const { user, loading } = useAuth()

  const [adminMode, setAdminMode] = useState(
    () => sessionStorage.getItem('admin-home-mode') === 'true'
  )

  const [open, setOpen] = useState(false)
  const [cycle, setCycle] = useState(0)
  const [hasBanner, setHasBanner] = useState(false)
  const [restartActiveBanner, setRestartActiveBanner] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const [autoCycleEnabled, setAutoCycleEnabled] = useState(() => (
    getSessionEnabled()
  ))

  const [boxStyle, setBoxStyle] = useState(() =>
    getFloatingBoxStyle(false)
  )

  const [displayHistory, setDisplayHistory] = useState(() => (
    getStoredState().displayHistory
  ))

  const [activeCycleEndsAt, setActiveCycleEndsAt] = useState(() => (
    getStoredState().activeCycleEndsAt
  ))

  const isLoggedIn = Boolean(user) && !loading

  const startDisplayCycle = useCallback((now, recentDisplays) => {
    const nextHistory = [...recentDisplays, now]
    const nextActiveCycleEndsAt = now + VISIBLE_MS

    setDisplayHistory(nextHistory)
    setActiveCycleEndsAt(nextActiveCycleEndsAt)

    setStoredState({
      displayHistory: nextHistory,
      activeCycleEndsAt: nextActiveCycleEndsAt
    })

    setExpanded(false)
    setOpen(true)
    setRestartActiveBanner(true)
    setCycle(prev => prev + 1)
  }, [])

  useEffect(() => {
    const handleAdminModeChange = (event) => {
      const enabled = event.detail === true

      setAdminMode(enabled)

      if (enabled) {
        setOpen(false)
        setHasBanner(false)
      }
    }

    window.addEventListener(
      'toggle-admin-home-buttons',
      handleAdminModeChange
    )

    return () => {
      window.removeEventListener(
        'toggle-admin-home-buttons',
        handleAdminModeChange
      )
    }
  }, [])

  useEffect(() => {
    const updateBoxStyle = () => {
      setBoxStyle(getFloatingBoxStyle(expanded))
    }

    updateBoxStyle()
    window.addEventListener('resize', updateBoxStyle)

    return () => {
      window.removeEventListener('resize', updateBoxStyle)
    }
  }, [expanded])

  useEffect(() => {
    if (!isLoggedIn || adminMode) {
      setOpen(false)
      setHasBanner(false)
      return undefined
    }

    const storedState = getStoredState()
    const now = Date.now()

    if (storedState.activeCycleEndsAt > now && getSessionEnabled()) {
      const recentDisplays = getRecentDisplays(
        storedState.displayHistory,
        now
      )

      setDisplayHistory(recentDisplays)
      setActiveCycleEndsAt(storedState.activeCycleEndsAt)

      setStoredState({
        displayHistory: recentDisplays,
        activeCycleEndsAt: storedState.activeCycleEndsAt
      })

      setOpen(true)
      setRestartActiveBanner(false)
      setCycle(prev => prev + 1)
      setAutoCycleEnabled(true)

      return undefined
    }

    const recentDisplays = getRecentDisplays(
      storedState.displayHistory,
      now
    )

    setDisplayHistory(recentDisplays)
    setActiveCycleEndsAt(0)

    setStoredState({
      displayHistory: recentDisplays,
      activeCycleEndsAt: 0
    })

    const displayState = getAutoDisplayState(
      recentDisplays,
      now
    )

    if (!displayState.allowed) {
      setDisplayHistory(displayState.recentDisplays)

      setStoredState({
        displayHistory: displayState.recentDisplays,
        activeCycleEndsAt: 0
      })

      return undefined
    }

    setSessionEnabled()
    setAutoCycleEnabled(true)
    startDisplayCycle(now, displayState.recentDisplays)

    return undefined
  }, [
    isLoggedIn,
    adminMode,
    startDisplayCycle
  ])

  useEffect(() => {
    if (!isLoggedIn || adminMode || !open) {
      return undefined
    }

    const closeTimer = setTimeout(() => {
      setOpen(false)
      setHasBanner(false)

      setActiveCycleEndsAt(0)

      setStoredState({
        displayHistory,
        activeCycleEndsAt: 0
      })
    }, Math.max(0, activeCycleEndsAt - Date.now()))

    return () => clearTimeout(closeTimer)
  }, [
    isLoggedIn,
    adminMode,
    open,
    cycle,
    activeCycleEndsAt,
    displayHistory
  ])

  useEffect(() => {
    if (
      !isLoggedIn ||
      adminMode ||
      open ||
      !autoCycleEnabled
    ) {
      return undefined
    }

    const reopenTimer = setTimeout(() => {
      const storedState = getStoredState()
      const now = Date.now()

      const displayState = getAutoDisplayState(
        storedState.displayHistory,
        now
      )

      if (!displayState.allowed) {
        setDisplayHistory(displayState.recentDisplays)

        setStoredState({
          displayHistory: displayState.recentDisplays,
          activeCycleEndsAt: 0
        })

        return
      }

      startDisplayCycle(
        now,
        displayState.recentDisplays
      )
    }, REOPEN_MS)

    return () => clearTimeout(reopenTimer)
  }, [
    isLoggedIn,
    adminMode,
    open,
    cycle,
    startDisplayCycle,
    autoCycleEnabled
  ])

  useEffect(() => {
    const handlePromoBanner = () => {
      if (adminMode || open) return

      const storedState = getStoredState()
      const now = Date.now()

      if (storedState.activeCycleEndsAt > now) {
        setActiveCycleEndsAt(
          storedState.activeCycleEndsAt
        )

        setOpen(true)
        setRestartActiveBanner(false)
        setCycle(prev => prev + 1)
        setSessionEnabled()
        setAutoCycleEnabled(true)

        return
      }

      const displayState = getManualDisplayState(
        storedState.displayHistory,
        now
      )

      if (!displayState.allowed) {
        setDisplayHistory(
          displayState.recentDisplays
        )

        setStoredState({
          displayHistory:
            displayState.recentDisplays,
          activeCycleEndsAt: 0
        })

        return
      }

      setSessionEnabled()
      setAutoCycleEnabled(true)

      startDisplayCycle(
        now,
        displayState.recentDisplays
      )
    }

    window.addEventListener(
      SHOW_PROMO_BANNER_EVENT,
      handlePromoBanner
    )

    return () => {
      window.removeEventListener(
        SHOW_PROMO_BANNER_EVENT,
        handlePromoBanner
      )
    }
  }, [
    adminMode,
    open,
    startDisplayCycle
  ])

  const handleItemsChange = useCallback((items) => {
    setHasBanner(
      Array.isArray(items) &&
      items.length > 0
    )
  }, [])

  const containerStyle = useMemo(
    () => ({
      ...styles.container,
      ...boxStyle,
      opacity: hasBanner ? 1 : 0,
      pointerEvents: hasBanner
        ? 'auto'
        : 'none'
    }),
    [
      boxStyle,
      hasBanner
    ]
  )

  if (
    !isLoggedIn ||
    adminMode ||
    !open
  ) {
    return null
  }

  return (
    <div
      style={containerStyle}
      aria-hidden={!hasBanner}
    >
      <PromoBannerModal
        key={cycle}
        fallback={null}
        placement="home"
        rememberFirstBanner={false}
        autoRotate={true}
        restartActiveBanner={restartActiveBanner}
        onItemsChange={handleItemsChange}
        showFullScreenButton={false}
      />

      <button
        type="button"
        aria-label={
          expanded
            ? 'Zmenšiť banner'
            : 'Zväčšiť banner'
        }
        title={
          expanded
            ? 'Zmenšiť banner'
            : 'Zväčšiť banner'
        }
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()

          setExpanded(value => !value)
        }}
        style={styles.expandButton}
      >
        {expanded ? '×' : '⛶'}
      </button>
    </div>
  )
}

const styles = {
  container: {
    position: 'fixed',
    zIndex: FLOATING_BANNER_Z_INDEX,
    overflow: 'hidden',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.42)',
    boxShadow: '0 12px 30px rgba(0,0,0,0.32)',
    background: 'rgba(9, 18, 14, 0.92)',
    transition:
      'opacity 180ms ease, width 180ms ease, height 180ms ease',
    userSelect: 'none'
  },

  expandButton: {
    position: 'absolute',
    top: 6,
    left: 6,
    width: 28,
    height: 28,
    padding: 0,
    border: 0,
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.62)',
    color: '#fff',
    fontSize: 17,
    lineHeight: '28px',
    textAlign: 'center',
    cursor: 'pointer',
    zIndex: FLOATING_BANNER_Z_INDEX
  }
}

export default FloatingPromoBanner